import type { ReactNode } from 'react'
import { BarChart3, Users } from 'lucide-react'
import type { CloserLeadSummary } from '@/types'

type BreakdownRow = { label: string; count: number }

function BreakdownList({
  title,
  icon,
  rows,
  accent,
}: {
  title: string
  icon: ReactNode
  rows: BreakdownRow[]
  accent: string
}) {
  if (rows.length === 0) return null
  const maxCount = Math.max(...rows.map((r) => r.count), 1)

  return (
    <div className='min-w-0 space-y-3'>
      <div className='flex items-center gap-2'>
        {icon}
        <span className='text-xs font-semibold text-text-primary'>{title}</span>
      </div>
      <div className='space-y-2'>
        {rows.map((item) => {
          const widthPct = Math.max((item.count / maxCount) * 100, 8)
          return (
            <div key={item.label} className='space-y-0.5'>
              <div className='flex items-center justify-between gap-2'>
                <span className='truncate text-[11px] font-medium text-text-muted'>{item.label}</span>
                <span className='shrink-0 text-[11px] font-bold text-text-primary'>{item.count}</span>
              </div>
              <div className='h-1.5 overflow-hidden rounded-full bg-border-subtle'>
                <div
                  className='h-full rounded-full'
                  style={{
                    width: `${widthPct}%`,
                    background: `linear-gradient(90deg, ${accent}20, ${accent}60)`,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface CloserMetricsBreakdownProps {
  summary: CloserLeadSummary
  isAdminOrManager?: boolean
}

export function CloserMetricsBreakdown({ summary, isAdminOrManager }: CloserMetricsBreakdownProps) {
  const originRows: BreakdownRow[] = (summary.byOrigin ?? [])
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)
    .map((item) => ({ label: item.origem, count: item.count }))

  const closerRows: BreakdownRow[] =
    isAdminOrManager && summary.byCloser
      ? summary.byCloser
          .sort((a, b) => b.count - a.count)
          .slice(0, 8)
          .map((item) => ({ label: item.closerName, count: item.count }))
      : []

  if (originRows.length === 0 && closerRows.length === 0) return null

  const twoColumns = originRows.length > 0 && closerRows.length > 0

  return (
    <div className='glass-card p-4'>
      <p className='mb-3 text-[10px] font-bold uppercase tracking-wider text-text-muted'>
        Distribuição
      </p>
      <div className={twoColumns ? 'grid grid-cols-1 gap-6 md:grid-cols-2' : 'max-w-md'}>
        <BreakdownList
          title='Por origem'
          icon={<BarChart3 size={14} className='text-accent' />}
          rows={originRows}
          accent='#14bdbd'
        />
        <BreakdownList
          title='Por closer'
          icon={<Users size={14} className='text-accent' />}
          rows={closerRows}
          accent='#3b82f6'
        />
      </div>
    </div>
  )
}
